import $ from 'jquery'

/**
 * Manages rendering of sleeving selector results
 * Handles DOM creation, transitions, and event binding for result cards
 */
export class ResultsRenderer {
  /**
   * @param {HTMLElement} resultsTarget - Container for result cards
   * @param {HTMLElement} matchCountTarget - Element displaying match count
   * @param {HTMLElement} rootElement - Root element for DOM queries
   * @param {Object} colorSchemes - Color scheme definitions
   * @param {Object} pieChartRenderer - Pie chart renderer instance
   * @param {Object} modalManager - Modal manager instance
   * @param {boolean} isMobileDevice - Whether device is mobile
   * @param {Object} messages - Translated message strings
   */
  constructor(
    resultsTarget,
    matchCountTarget,
    rootElement,
    colorSchemes,
    pieChartRenderer,
    modalManager,
    isMobileDevice,
    messages
  ) {
    this.resultsTarget = resultsTarget
    this.matchCountTarget = matchCountTarget
    this.rootElement = rootElement
    this.colorSchemes = colorSchemes
    this.pieChartRenderer = pieChartRenderer
    this.modalManager = modalManager
    this.isMobileDevice = isMobileDevice
    this.messages = messages
  }

  /**
   * Render results with product cards
   * @param {Array} data - Array of product data objects
   */
  renderResults(data) {
    this.updateMatchCount(data.length)

    const renderCards = () => {
      $(this.resultsTarget).empty()
      data.forEach(item => {
        this.createPieCanvas(item)
        this.pieChartRenderer.makePie(item)
      })
    }

    if (this.isMobileDevice) {
      renderCards()
    } else {
      this.fadeOutAndRender(renderCards, 500)
    }
  }

  /**
   * Render error state
   */
  renderError() {
    this.updateMatchCount(0)

    const renderErrorMessage = () => {
      $(this.resultsTarget).empty()
      $(this.resultsTarget).append(`<h2><b>${this.messages.noResultsMessage}</b></h2>`)
    }

    if (this.isMobileDevice) {
      renderErrorMessage()
    } else {
      this.fadeOutAndRender(renderErrorMessage, 100)
    }
  }

  /**
   * Update match count display
   * @param {number} count - Number of matches
   */
  updateMatchCount(count) {
    if (count > 0) {
      this.matchCountTarget.innerHTML = `<b>${count} ${this.messages.matchesText}</b>`
    } else {
      this.matchCountTarget.innerHTML = `<b>${this.messages.noMatchesText}</b><br><br><h2>${this.messages.noResultsMessage}</h2>`
    }
  }

  /**
   * Fade out results, execute callback, then fade in
   * @param {Function} callback - Function to execute while faded out
   * @param {number} fadeInDuration - Duration of fade in animation
   */
  fadeOutAndRender(callback, fadeInDuration) {
    $(this.resultsTarget).fadeOut(200, () => {
      callback()
      $(this.resultsTarget).fadeIn(fadeInDuration)
    })
  }

  /**
   * Create a pie chart canvas card for a product
   * @param {Object} data - Product data object
   */
  createPieCanvas(data) {
    const html = `
      <div class='col-sm-6 col-md-4 col-lg-3'>
        <div class='piechart-card' data-bs-toggle='modal' data-bs-target='#modal-pie' role='button' tabindex='0' aria-label='View details for ${data.chart_header}'>
          <h3 class='piechart-title ${data.product_name}_header'>${data.chart_header}</h3>
          <canvas id='${data.product_name}' width='200' height='200' role='img' aria-label='${data.chart_header} product chart'></canvas>
        </div>
      </div>
    `
    $(this.resultsTarget).append(html)
    
    this.styleCardHeader(data)
    this.bindCardEvents(data)
  }

  /**
   * Apply color scheme styling to card header
   * @param {Object} data - Product data object
   */
  styleCardHeader(data) {
    const headerEl = this.rootElement.querySelector(`.${data.product_name}_header`)
    if (headerEl) {
      headerEl.style.color = this.colorSchemes[data.color_scheme].dark
    }
  }

  /**
   * Bind click and keyboard events to card
   * @param {Object} data - Product data object
   */
  bindCardEvents(data) {
    const cardEl = this.rootElement.querySelector(`canvas#${data.product_name}`)?.closest('.piechart-card')
    if (!cardEl) return

    const openModal = () => this.modalManager.openModal(data)
    
    cardEl.onclick = openModal
    cardEl.onkeydown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openModal()
      }
    }
  }
}
